/**
 * Captura de ubicación en paralelo (004 · US6 · T065; FR-003, FR-031; research R-24).
 *
 * **Nunca bloquea el VPP.** Se dispara en el mismo instante en que se abre la cámara (R-24), con
 * `enableHighAccuracy: true` y un `timeout` **configurable** (4 s por defecto, el mismo valor que
 * `GEO_TIMEOUT_MS` del servidor). Si al enviar la evidencia la posición no llegó todavía, se envía
 * sin ella (`location: null`); si llega **después** de que ya se decidió no esperarla, se
 * **descarta** — nunca se usa "por si acaso" en un intento distinto del que la pidió (FR-031: las
 * coordenadas se capturan únicamente durante la marcación en curso).
 *
 * **Cuándo empieza a contar el plazo propio (QA-3 de la 005).** El plazo mide cuánto tarda el
 * GPS, **no** cuánto tarda la persona en contestar el diálogo de permiso. Antes arrancaba en el
 * mismo instante en que se abría la cámara, así que en la **primera** marcación —con los diálogos
 * de cámara y de ubicación abiertos a la vez y el GPS frío— se consumía entero mientras el
 * trabajador leía y tocaba "Permitir", y la marca salía sin ubicación aunque la concediera. Ahora:
 *
 * - se consulta la Permissions API (`permissions.query({ name: 'geolocation' })`) **en paralelo**
 *   con `getCurrentPosition` (la petición sale igual en el mismo tick, R-24 se mantiene);
 * - `granted` o `denied` → el plazo arranca en cuanto se sabe (con `denied` el propio navegador
 *   llama al callback de error enseguida);
 * - `prompt` → el plazo **no** arranca hasta que el estado cambie (evento `change` del
 *   `PermissionStatus`), es decir, hasta que el trabajador conteste el diálogo;
 * - sin Permissions API, o si `query` lanza/rechaza (Firefox antiguo, WebView), se conserva el
 *   comportamiento de siempre: el plazo arranca de inmediato.
 *
 * Mientras el diálogo sigue abierto la promesa queda pendiente, y eso está bien: el llamador
 * (`use-marking.ts`) nunca la espera más de su propio margen (`LOCATION_GRACE_MS`) y descarta lo
 * que llegue tarde por generación. Si el navegador no emitiera nunca el `change` (algunos Safari),
 * el respaldo es el `timeout` de `getCurrentPosition`, que por especificación (W3C Geolocation
 * §6.3) **tampoco** cuenta el tiempo de espera del permiso: el callback de error llega igual.
 *
 * El descarte de la respuesta tardía vive en dos sitios, a propósito:
 * 1. Aquí mismo: pasado `timeoutMs` se resuelve `{ status: 'unavailable' }` y cualquier callback
 *    del navegador que llegue después se ignora (bandera `settled`), así que una promesa de
 *    `captureLocation` nunca se resuelve dos veces ni tarde.
 * 2. En `use-marking.ts`: el llamador etiqueta cada intento con un número de generación y descarta
 *    el resultado si, para cuando la promesa se resuelve, la cámara ya se cerró o se reabrió para
 *    un intento nuevo.
 */

/** Motivo por el que no hubo lectura utilizable; no se distingue en la interfaz (FR-029 los trata igual). */
export type GeoUnavailableReason = 'DENIED' | 'TIMEOUT' | 'ERROR' | 'UNSUPPORTED';

export interface GeoSample {
  latitude: number;
  longitude: number;
  /** Metros; redondeado (telemetría y umbral de precisión comparan sobre este valor). */
  accuracyM: number;
  /** Milisegundos desde que se pidió la ubicación hasta que se obtuvo (telemetría de `LOCATION_RESULT`). */
  latencyMs: number;
}

export type GeoOutcome =
  | { status: 'ok'; sample: GeoSample }
  | { status: 'unavailable'; reason: GeoUnavailableReason };

/** Mismo valor que `GEO_TIMEOUT_MS` por defecto del servidor (wiring §6); configurable por el llamador. */
export const DEFAULT_GEO_TIMEOUT_MS = 4_000;

/** Tope del contrato para `latency_ms` (`LocationSampleSchema`). */
const MAX_LATENCY_MS = 60_000;

/** Lo mínimo de `navigator.permissions` que usa este módulo (inyectable en pruebas). */
export type GeoPermissionsLike = Pick<Permissions, 'query'>;

export interface CaptureLocationOptions {
  timeoutMs?: number;
  /** Inyectable en pruebas; por defecto `navigator.geolocation`. */
  geolocation?: Pick<Geolocation, 'getCurrentPosition'>;
  /**
   * QA-3: Permissions API para saber si el permiso de ubicación ya está concedido y arrancar el
   * plazo recién entonces. Por defecto `navigator.permissions`; `null` la desactiva (el plazo
   * arranca de inmediato, el comportamiento anterior).
   */
  permissions?: GeoPermissionsLike | null;
  /** Reloj monotónico para medir `latencyMs`; por defecto `performance.now()`. */
  now?: () => number;
}

function defaultNow(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

function defaultPermissions(): GeoPermissionsLike | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const permissions = (navigator as { permissions?: Permissions }).permissions;
  return permissions !== undefined && typeof permissions.query === 'function' ? permissions : null;
}

const NO_OP = (): void => undefined;

/**
 * QA-3: arranca el plazo (`start`) según el estado del permiso de ubicación. Devuelve la función
 * que deja de escuchar el `change`. Nunca lanza: cualquier fallo de la Permissions API arranca el
 * plazo de inmediato (el comportamiento anterior).
 */
async function watchPermission(
  permissions: GeoPermissionsLike,
  start: () => void,
): Promise<() => void> {
  let status: PermissionStatus;
  try {
    status = await permissions.query({ name: 'geolocation' });
  } catch {
    start();
    return NO_OP;
  }
  if (status.state !== 'prompt') {
    start();
    return NO_OP;
  }
  const onChange = (): void => {
    if (status.state !== 'prompt') {
      start();
    }
  };
  if (typeof status.addEventListener === 'function') {
    status.addEventListener('change', onChange);
    return () => {
      status.removeEventListener('change', onChange);
    };
  }
  // Sin `addEventListener` (implementaciones antiguas): `onchange`.
  status.onchange = onChange;
  return () => {
    status.onchange = null;
  };
}

function reasonOf(error: unknown): GeoUnavailableReason {
  const code = typeof error === 'object' && error !== null ? (error as { code?: number }).code : undefined;
  // `GeolocationPositionError`: 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT.
  if (code === 1) {
    return 'DENIED';
  }
  if (code === 3) {
    return 'TIMEOUT';
  }
  return 'ERROR';
}

/**
 * Pide una lectura de alta precisión con un plazo propio. **Nunca lanza**: toda salida es un
 * `GeoOutcome`, para que el llamador nunca tenga que envolver la marcación en un `try/catch` por
 * culpa de la ubicación (FR-003).
 */
export function captureLocation(options: CaptureLocationOptions = {}): Promise<GeoOutcome> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_GEO_TIMEOUT_MS;
  const now = options.now ?? defaultNow;
  const geolocation =
    options.geolocation ??
    (typeof navigator !== 'undefined' ? navigator.geolocation : undefined);

  if (!geolocation || typeof geolocation.getCurrentPosition !== 'function') {
    return Promise.resolve({ status: 'unavailable', reason: 'UNSUPPORTED' });
  }

  const permissions =
    options.permissions === undefined ? defaultPermissions() : options.permissions;

  const startedAt = now();

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof globalThis.setTimeout> | null = null;
    let stopWatchingPermission: (() => void) | null = null;

    const finish = (outcome: GeoOutcome): void => {
      // La respuesta tardía (el temporizador de arriba ya resolvió) se descarta en silencio.
      if (settled) {
        return;
      }
      settled = true;
      if (timer !== null) {
        globalThis.clearTimeout(timer);
      }
      stopWatchingPermission?.();
      stopWatchingPermission = null;
      resolve(outcome);
    };

    /** Arranca el plazo propio una sola vez (QA-3: cuando el permiso ya no está en `prompt`). */
    const startTimer = (): void => {
      if (settled || timer !== null) {
        return;
      }
      timer = globalThis.setTimeout(() => {
        finish({ status: 'unavailable', reason: 'TIMEOUT' });
      }, timeoutMs);
    };

    if (permissions === null) {
      startTimer();
    } else {
      void watchPermission(permissions, startTimer).then((stop) => {
        if (settled) {
          stop();
          return;
        }
        stopWatchingPermission = stop;
      });
    }

    geolocation.getCurrentPosition(
      (position) => {
        const latencyMs = Math.min(MAX_LATENCY_MS, Math.max(0, Math.round(now() - startedAt)));
        finish({
          status: 'ok',
          sample: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: Math.round(position.coords.accuracy),
            latencyMs,
          },
        });
      },
      (error) => {
        finish({ status: 'unavailable', reason: reasonOf(error) });
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}
